import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Observable from 'zen-observable-ts';

import { deleteGiide, updateSubscription } from '../../actions';
import { IState, Step, SubscriptionsState } from '../../graphql/types';
import useGiideList from '../useQuery/useGiideList';
import useUser from '../useUser';
import { DocumentNode } from 'graphql';

const onCreateGiideAdmin = gql`
  subscription OnCreateGiideAdmin($tenantID: String) {
    onCreateGiideAdmin(tenantID: $tenantID) {
      id
      tenantID
    }
  }
`;

const onCreateGiideAuthor = gql`
  subscription OnCreateGiideAuthor($owner: String) {
    onCreateGiideAuthor(owner: $owner) {
      id
      owner
    }
  }
`;

const onUpdateGiide = gql`
  subscription OnUpdateGiide($tenantID: String) {
    onUpdateGiide(tenantID: $tenantID) {
      id
      tenantID
    }
  }
`;

const onPreviewTrigger = gql`
  subscription OnPreviewTrigger($owner: String) {
    onPreviewTrigger(owner: $owner) {
      id
      owner
    }
  }
`;

const onDeleteGiideAdmin = gql`
  subscription OnDeleteGiideAdmin($tenantID: String) {
    onDeleteGiideAdmin(tenantID: $tenantID) {
      id
      tenantID
    }
  }
`;

const onDeleteGiideAuthor = gql`
  subscription OnDeleteGiideAuthor($owner: String) {
    onDeleteGiideAuthor(owner: $owner) {
      id
      owner
    }
  }
`;

const useGiideSubscriptions = () => {
  const dispatch = useDispatch();
  const { refetch } = useGiideList(true);
  const { isAdmin, loading, user } = useUser();
  const steps = useSelector<IState, Step[]>(state => state.steps);
  const subs = useSelector<IState, SubscriptionsState>(
    state => state.subscriptions
  );

  const subsToUse = React.useMemo<DocumentNode[]>(
    () =>
      isAdmin
        ? [onCreateGiideAdmin, onUpdateGiide, onDeleteGiideAdmin]
        : [onCreateGiideAuthor, onPreviewTrigger, onDeleteGiideAuthor],
    [isAdmin]
  );

  const variablesToUse = React.useMemo<object>(
    () => (isAdmin ? { tenantID: user?.tenantID } : { owner: user?.userId }),
    [user, isAdmin]
  );

  React.useEffect(() => {
    if (!loading && user?.userId && Object.keys(subs).length < 4) {
      const createObs = API.graphql(
        graphqlOperation(subsToUse[0], variablesToUse)
      );
      const updateObs = API.graphql(
        graphqlOperation(subsToUse[1], variablesToUse)
      );
      const deleteObs = API.graphql(
        graphqlOperation(subsToUse[2], variablesToUse)
      );
      if (createObs instanceof Observable) {
        const createSub = createObs.subscribe({
          next: () => refetch(true),
          complete: () => console.log('completed create giide subscription'),
          error: console.log,
        });
        dispatch(updateSubscription('createGiide', createSub));
      }
      if (updateObs instanceof Observable) {
        const updateSub = updateObs.subscribe({
          next: () => refetch(true),
          complete: () => console.log('completed update giide subscription'),
          error: console.log,
        });
        dispatch(updateSubscription('updateGiide', updateSub));
      }
      if (deleteObs instanceof Observable) {
        const deleteSub = deleteObs.subscribe({
          next: (result: { value: { data: any } }) => {
            const giideId = isAdmin
              ? result.value.data.onDeleteGiideAdmin?.id
              : result.value.data.onDeleteGiideAuthor?.id;
            dispatch(deleteGiide(giideId));
          },
          complete: () => console.log('completed delete giide subscription'),
          error: console.log,
        });
        dispatch(updateSubscription('deleteGiide', deleteSub));
      }
    }
  }, [
    subsToUse,
    subs,
    steps,
    dispatch,
    user,
    loading,
    variablesToUse,
    isAdmin,
    refetch,
  ]);

  return subs;
};

export default useGiideSubscriptions;
